import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ServiceHistoryRecord, PlanChangeHistoryEntry } from '../../shared/types';
import { formatVehicleLine } from '../../shared/utils/format-vehicle-line.util';
import { OrderUtils } from '../../orders/utils/order.utils';
import { resolveServiceHistorySourceChip } from './service-history-source.util';
import {
  countServiceHistoryRowsByOrder,
  resolveServiceHistoryRowPrice,
} from './service-history-price.util';

export interface ServiceRecord {
  id: string;
  orderId: string;
  packageName: string;
  price: string;
  date: string;
  rawDate: string;
  vehicle: string;
  location: string;
  paymentMethod?: string;
  paymentIntentId?: string;
  paymentDate?: string;
  discountAmount?: number;
  discountName?: string;
  discountCode?: string;
  addons?: { name: string; price: number; count: number }[];
  shopName?: string;
  shopEmail?: string;
  shopContact?: string;
  sourceLabel: string;
  sourceChipVariant: 'grey' | 'blue';
  /** CWS-1444: true when the row is a membership plan change, not a wash. */
  isPlanChangeRecord?: boolean;
  /** "Plan changed from X to Y — immediately — by Z", built server-side. */
  planChangeLine?: string;
  /** `pending` | `applied` | `cancelled` | `failed`. */
  planChangeStatus?: string;
  /** "Charged $40.00" / "Refunded $4.50", or absent when no money moved. */
  planChangeMoney?: string;
  /** The refund the attendant picked, in the Update screen's words. */
  planChangeRefundOption?: string;
  /** Washes the change left for the rest of the cycle, or "Unlimited". */
  planChangeWashes?: string;
}

/**
 * Displays a customer's service history with search and invoice download support.
 * CWS-1734: imported (vendor) memberships are no longer listed here; they are cards in
 * the Subscriptions tab. This tab is wash history and plan changes for every customer.
 * @component
 * @selector app-service-history
 * @example
 *   <app-service-history [customerId]="selectedCustomerId" (downloadInvoice)="onDownloadInvoice($event)"></app-service-history>
 *
 * @input {string} customerId - The ID of the customer whose history will be fetched.
 * @output {EventEmitter<ServiceRecord>} downloadInvoice - Emits when the user requests an invoice download.
 */
@Component({
  selector: 'app-service-history',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './service-history.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./service-history.component.scss'],
})
export class ServiceHistoryComponent implements OnInit {
  @Input() customerId = '';
  @Output() downloadInvoice = new EventEmitter<ServiceRecord>();
  @Output() printInvoice = new EventEmitter<ServiceRecord>();

  searchQuery = '';
  serviceRecords: ServiceRecord[] = [];
  filteredRecords: ServiceRecord[] = [];
  loading = false;
  error = '';

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    if (this.customerId) {
      this.loadServiceHistory();
    }
  }

  loadServiceHistory(): void {
    this.loadHistory();
  }

  loadHistory(): void {
    this.loading = true;
    this.error = '';

    // CWS-1444: washes and plan changes are one story, so they are fetched
    // together and interleaved by date. The plan-change call degrades to an
    // empty list rather than failing the tab: a member's wash history is the
    // reason this screen exists, and losing it because the newer endpoint is
    // unavailable would be a worse outcome than missing the plan changes.
    forkJoin({
      washes: this.apiService.getServiceHistory(this.customerId),
      planChanges: this.apiService.getCustomerPlanChangeHistory(this.customerId).pipe(
        map(response => (response.status && response.data ? response.data.changes : [])),
        catchError(error => {
          console.error('Error loading plan change history:', error);
          return of([] as PlanChangeHistoryEntry[]);
        })
      ),
    }).subscribe({
      next: ({ washes, planChanges }) => {
        if (washes.status && washes.data) {
          this.serviceRecords = this.mergeByDateDesc(
            this.mapApiDataToServiceRecords(washes.data),
            this.mapPlanChangesToServiceRecords(planChanges)
          );
          this.filteredRecords = [...this.serviceRecords];
        } else {
          this.error = washes.message || 'Failed to load service history';
        }
        this.loading = false;
      },
      error: error => {
        console.error('Error loading service history:', error);
        this.error = 'Failed to load service history. Please try again.';
        this.loading = false;
      },
    });
  }

  /**
   * Interleave plan changes into the wash history, newest first.
   *
   * The wash list is returned UNTOUCHED when there is nothing to interleave,
   * and that is deliberate. The server orders washes by `orders.created_at`,
   * while `rawDate` is `payment_date || appointment_time || created_at` — so
   * sorting the list by `rawDate` is not the same ordering, and applying it
   * unconditionally would quietly re-order the history of every customer who
   * has never changed plan. Re-ordering is the price of mixing the two kinds,
   * and it is only paid when they actually are mixed.
   *
   * A row with no usable date sorts last rather than to the top, where an
   * unparseable string would otherwise land.
   */
  private mergeByDateDesc(washes: ServiceRecord[], planChanges: ServiceRecord[]): ServiceRecord[] {
    if (!planChanges.length) {
      return washes;
    }
    const time = (record: ServiceRecord): number => {
      const parsed = new Date(record.rawDate).getTime();
      return Number.isNaN(parsed) ? Number.NEGATIVE_INFINITY : parsed;
    };
    return [...washes, ...planChanges].sort((a, b) => time(b) - time(a));
  }

  /**
   * A plan change as a history row.
   *
   * The sentence, the money and the allowance all come from the API rather than
   * being rebuilt here, so the customer page, the service history and the
   * Orders entry cannot describe the same change three different ways.
   */
  private mapPlanChangesToServiceRecords(changes: PlanChangeHistoryEntry[]): ServiceRecord[] {
    return (changes || []).map(change => ({
      id: change.id,
      orderId: '',
      // The plan they ended on is what staff scan for.
      packageName: change.new_package.name,
      price: change.money_line ? `$${change.money_line.amount.toFixed(2)}` : '',
      date: this.formatDate(change.created_at),
      rawDate: change.created_at,
      vehicle: change.license_plate || '—',
      location: change.location_name || '—',
      sourceLabel: change.is_upgrade ? 'Upgrade' : 'Downgrade',
      sourceChipVariant: 'blue' as const,
      isPlanChangeRecord: true,
      planChangeLine: change.history_line,
      planChangeStatus: change.status,
      planChangeMoney: change.money_line
        ? `${change.money_line.label} $${change.money_line.amount.toFixed(2)}`
        : undefined,
      planChangeRefundOption: change.refund_option_label || undefined,
      planChangeWashes: this.planChangeWashesLabel(change),
    }));
  }

  /**
   * Washes left for the rest of the cycle.
   *
   * Unlimited and "not applied yet" both arrive as a null count and mean
   * opposite things, so they are never collapsed into one label.
   */
  private planChangeWashesLabel(change: PlanChangeHistoryEntry): string | undefined {
    if (change.washes_this_cycle_unlimited) {
      return 'Unlimited';
    }
    if (change.washes_this_cycle === null || change.washes_this_cycle === undefined) {
      return undefined;
    }
    return change.washes_this_cycle === 1 ? '1 wash' : `${change.washes_this_cycle} washes`;
  }

  private mapApiDataToServiceRecords(apiData: ServiceHistoryRecord[]): ServiceRecord[] {
    const rowsByOrder = countServiceHistoryRowsByOrder(apiData);
    return apiData.map(record => {
      const isWayOrder = OrderUtils.isWayRedeemedOrder(record);
      const sourceChip = resolveServiceHistorySourceChip(record);
      const priceValue = resolveServiceHistoryRowPrice(record, rowsByOrder.get(record.id) ?? 1);
      const serviceDate = record.payment_date || record.appointment_time || record.created_at || '';

      return {
        id: record.id,
        orderId: (record.order_id || record.id).replace(/^#+/, ''),
        packageName: record.package_name,
        price: `$${priceValue}`,
        date: this.formatDate(serviceDate),
        rawDate: serviceDate,
        vehicle: formatVehicleLine(record.year, record.make, record.model),
        location: record.branch,
        paymentMethod: record.payment_method || (isWayOrder ? 'Way' : undefined),
        paymentIntentId: record.payment_intent_id,
        paymentDate: serviceDate,
        discountAmount: record.discount_amount ? Number(record.discount_amount) : 0,
        discountName: record.discount_name,
        discountCode: record.discount_code,
        addons: record.addons?.map(a => ({
          name: a.addon_name,
          price: a.custom_price,
          count: a.count,
        })),
        shopName: record.shop_name,
        shopEmail: record.shop_email,
        shopContact: record.shop_contact,
        sourceLabel: sourceChip.label,
        sourceChipVariant: sourceChip.variant,
      };
    });
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString || '—';
    }
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  onSearchChange(): void {
    if (!this.searchQuery.trim()) {
      this.filteredRecords = [...this.serviceRecords];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredRecords = this.serviceRecords.filter(
      record =>
        record.packageName.toLowerCase().includes(query) ||
        record.sourceLabel.toLowerCase().includes(query) ||
        record.vehicle.toLowerCase().includes(query) ||
        record.location.toLowerCase().includes(query) ||
        record.date.toLowerCase().includes(query) ||
        (record.price || '').toLowerCase().includes(query) ||
        (record.planChangeLine || '').toLowerCase().includes(query) ||
        (record.planChangeMoney || '').toLowerCase().includes(query)
    );
  }

  // Both take the clicked row itself, not its id: `id` is the order, and a membership's renewals
  // are rows of the same order, so looking the id up again always found the first payment.
  onDownloadInvoice(record: ServiceRecord): void {
    if (record && !record.isPlanChangeRecord) {
      this.downloadInvoice.emit(record);
    }
  }

  onPrintInvoice(record: ServiceRecord): void {
    if (record && !record.isPlanChangeRecord) {
      this.printInvoice.emit(record);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
